//---------------------------------------------------------
// Soft #40: pure helpers that shape chart-proxy results for the cards.
//---------------------------------------------------------
import {IMetricsChartResult} from 'types/metricsCharts';

export function seriesByKey(result: IMetricsChartResult | undefined, key: string) {
	return result?.series.find(s => s.key === key);
}

export function hasSamples(result: IMetricsChartResult | undefined, key?: string): boolean {
	if (!result) return false;
	const list = key ? [seriesByKey(result, key)] : result.series;
	return list.some(s => !!s && s.samples.length > 0);
}

// Sum of one series across scrape targets; null when there are no samples.
export function sumSeries(result: IMetricsChartResult | undefined, key: string): number | null {
	const s = seriesByKey(result, key);
	if (!s || s.samples.length === 0) return null;
	return s.samples.reduce((acc, x) => acc + x.value, 0);
}

export function serviceLabel(raw: string | undefined, unnamed: string): string {
	return !raw || raw === '-' ? unnamed : raw;
}

export interface IPieSlice {
	id: number;
	label: string;
	value: number; // percent of the total, slices sum to exactly 100
}

// Traffic Distribution by Service: shares normalized to percent of the sum so
// the pie is whole. Zero / negative shares drop out.
export function toServicePie(result: IMetricsChartResult | undefined, unnamed: string): IPieSlice[] {
	const s = seriesByKey(result, 'share');
	if (!s) return [];
	const rows = s.samples.filter(x => x.value > 0);
	const total = rows.reduce((a, x) => a + x.value, 0);
	if (total <= 0) return [];
	const slices = rows.map((x, i) => ({id: i, label: serviceLabel(x.labels.service, unnamed), value: (x.value / total) * 100}));
	// Float drift must not make PieChartWithTitle add an "Available" slice.
	const drift = 100 - slices.reduce((a, x) => a + x.value, 0);
	slices[slices.length - 1].value += drift;
	return slices;
}

export interface IBarRow {
	label: string;
	sublabel?: string;
	value: number;
	percent: number; // 0..100 for the bar
}

// Backend balance: per-backend share of its service (ratio 0..1). The OAM proxy
// already limited the rows to endpoints registered in the instance's LoxiLB LB
// rules (a silent registered backend comes back as 0).
export function toBackendRows(result: IMetricsChartResult | undefined, unnamed: string): IBarRow[] {
	const s = seriesByKey(result, 'share');
	if (!s) return [];
	return s.samples.map(x => ({
		label: x.labels.dip ?? '',
		sublabel: serviceLabel(x.labels.service, unnamed),
		value: x.value,
		percent: Math.max(0, Math.min(100, x.value * 100)),
	}));
}

// Top Clients: packets per second by client (sip). Bars are relative to the
// busiest client.
export function toClientRows(result: IMetricsChartResult | undefined): IBarRow[] {
	const s = seriesByKey(result, 'pps');
	if (!s) return [];
	const max = s.samples.reduce((m, x) => Math.max(m, x.value), 0);
	return s.samples.map(x => ({
		label: x.labels.sip ?? '',
		value: x.value,
		percent: max > 0 ? Math.max(0, Math.min(100, (x.value / max) * 100)) : 0,
	}));
}
